import { useEffect, useState } from 'react';

import introImg from '../../../../assets/images/courses/collaboration-fundamentals/chapter3.1.jpg';
import chapterImg1 from '../../../../assets/images/courses/collaboration-fundamentals/chapter3.2.png';
import chapterImg2 from '../../../../assets/images/courses/collaboration-fundamentals/chapter3.3.jpg';
import chapterImg3 from '../../../../assets/images/courses/collaboration-fundamentals/chapter3.4.jpg';
import chapterImg4 from '../../../../assets/images/courses/collaboration-fundamentals/chapter3.5.jpg';
import { course_questions as courseQuestions } from '../../../../courses/collaboration-fundamentals/collaboration-fundamentals.quiz';
import { CourseChapter } from '../../../../courses/courses.types';
import {
  QuestionOption,
  QuizAnswer,
} from '../../../../courses/jumpstart-communicator/jumpstart-communicator.types';
import { useUserCourseQuestionResponseContext } from '../../../../hooks/use-user-course-question-response.hooks';
import {
  dispatchUpdateUserCourseQuestionResponse,
  UserChapterQuestion,
} from '../../../../redux/slice/user-course-question-response';
import { useAppDispatch } from '../../../../redux/store';
import { CourseQuestionCard } from '../../CourseQuestionCard';
import { MediaBlock } from '../../MediaBlock';
import { TabsBlock } from '../../TabsBlock';

interface CollaborationFundamentalsProps {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}
type AnswersState = {
  [questionId: string]: QuizAnswer;
};
export const CollaborationFundamentalsChapter3: React.FC<CollaborationFundamentalsProps> = ({
  chapter,
  onNextChapter,
  totalPages,
  // courseId,
}) => {
  const dispatch = useAppDispatch();
  const [, setAnswers] = useState<AnswersState>({});
  const [isPageDirty, setIsPageDirty] = useState(true);

  const userCourseQuestionResponseContext = useUserCourseQuestionResponseContext();
  const chapterQuestions = courseQuestions.filter(
    (courseChapter) => courseChapter.chapter_id === chapter.id,
  );

  const handleCourseAnswerSubmit = async (
    quizAnswers: QuestionOption[],
    isCorrect: boolean,
    questionId?: string,
  ) => {
    setAnswers((prevAnswers) => {
      if (questionId) {
        return {
          ...prevAnswers,
          [questionId]: {
            chapter_id: chapter.id,
            question_id: questionId,
            question: chapterQuestions[0].question,
            is_correct: isCorrect,
            answer_options: quizAnswers,
          },
        };
      } else {
        return { ...prevAnswers };
      }
    });

    const userQuestionAnswer: UserChapterQuestion = {
      question_id: questionId,
      isCorrect: isCorrect,
      question: chapterQuestions.find((x) => x.id === questionId)?.question,
      answers: quizAnswers,
    };
    const userAnswerResponse =
      userCourseQuestionResponseContext[chapterQuestions[0].course_id]?.chapters[chapter.id]
        ?.questions || [];

    await dispatch(
      dispatchUpdateUserCourseQuestionResponse({
        [chapterQuestions[0].course_id]: {
          chapters: {
            [chapter.id]: {
              questions: [
                ...userAnswerResponse.filter((x) => x.question_id !== questionId),
                userQuestionAnswer,
              ],
              quiz: null,
            },
          },
        },
      }),
    );
  };

  const handleQuestionRetake = async (questionId: string) => {
    setAnswers((prevAnswers) => {
      //eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [questionId]: _, ...newState } = prevAnswers;
      return newState;
    });
    const userAnswerResponse =
      userCourseQuestionResponseContext[chapterQuestions[0].course_id]?.chapters[chapter.id]
        ?.questions || [];

    await dispatch(
      dispatchUpdateUserCourseQuestionResponse({
        [chapterQuestions[0].course_id]: {
          chapters: {
            [chapter.id]: {
              questions: [...userAnswerResponse.filter((x) => x.question_id !== questionId)],
              quiz: null,
            },
          },
        },
      }),
    );
    setIsPageDirty(true);
  };
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };
  const handlePrint = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };
  useEffect(() => {
    const userAnswerResponse =
      userCourseQuestionResponseContext[chapterQuestions[0].course_id]?.chapters[chapter.id]
        ?.questions;
    if (Object.keys(userCourseQuestionResponseContext).length && userAnswerResponse?.length) {
      setIsPageDirty(false);
    }
  }, [userCourseQuestionResponseContext]);

  const showYoureListeningTabData = [
    {
      title: 'Collaboration at Work',
      mediaImage: chapterImg2,

      htmlContent: `<p>Once you join the workforce, your skill in navigating various work environments will greatly impact your success. Being able to collaborate effectively with others is crucial across all professions. Employers often prioritize teamwork skills above all else, according to national surveys.  <br/> <br/> Additionally, traits like self-motivation and initiative rank high, whether you're working solo, in a team, or in a leadership role. Collaboration in the workplace is key to achieving goals and solving problems collectively with your colleagues. </p>`,
    },
    {
      title: 'Collaboration at Home',
      mediaImage: chapterImg3,
      htmlContent: `<p>Our ability to collaborate doesn't just affect our professional lives; it also plays a significant role in our personal endeavors. Whether we're on sports teams, part of a band, or organizing community events or fundraisers, strong collaboration skills enhance our contributions and effectiveness. <br/> <br/> They also help us expand our social networks and open up opportunities for personal growth and development. Collaborating at home can also be beneficial, as it saves time and allows us to tackle problems together as a team. </p>`,
    },
    {
      title: 'Collaboration at School',
      mediaImage: chapterImg4,
      htmlContent: `<p>Collaboration at school is super important for both learning and personal growth. When you work with classmates on projects or assignments, you get better at communicating, solving problems, and being a team player. <br/> <br/> Sharing different ideas and perspectives makes your work more creative and effective. Plus, collaborating helps you build strong friendships and creates a supportive school vibe. By joining group activities and discussions, you learn to respect others' opinions, handle conflicts better, and work together to achieve common goals, which is great practice for future teamwork in life and work. </p>`,
    },
  ];

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Collaboration - A Critical Skill</h2>

        <MediaBlock mediaImage={introImg}></MediaBlock>

        <p className="text-xl text-center font-light flex-1 mt-5 mb-5">
          Collaboration is an important skill. Employers say the ability to work well in teams is
          critical. They really want employees who fit into the culture of the workplace – and being
          able to collaborate is an important part of that culture.
        </p>
        <p className="text-l font-semibold flex-1 mt-5 mb-5">
          If you do any of these, you can collaborate!
        </p>
        <img src={chapterImg1} alt="" />
        <p className="text-xl font-light flex-1 mt-5 mb-5">
          You can collaborate to help meet your goals. Collaboration is important at work, school
          and at home. Let’s look at each of these in more detail. Click each tab to learn more.
        </p>

        <TabsBlock tabsData={showYoureListeningTabData} />

        <h3 className="text-2xl text-purple-600 font-bold m-5">Reflect</h3>
        <p className="text-xl font-light flex-1 mt-5 mb-5">
          Complete the following questions, you may print your responses upon completion.
        </p>

        <div id="print-view">
          <ol className="list-decimal pl-7 mb-14 text-xl font-light">
            <li className="mb-5">
              {' '}
              <div>
                <p className="m-4">
                  What is an example of a time when you have collaborated?{' '}
                  <span className="text-purple-500">Idea bank</span>
                </p>
                <textarea
                  className="w-full h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </div>
            </li>
            <li className="mb-5">
              {' '}
              <div>
                <p className="m-4">What was the team's goal?</p>
                <textarea
                  className="w-full h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </div>
            </li>
            <li className="mb-5">
              {' '}
              <div>
                <p className="m-4">
                  How did collaborating help meet your goal?{' '}
                  <span className="text-purple-500">Idea bank</span>
                </p>
                <textarea
                  className="w-full h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </div>
            </li>
          </ol>

          <div className="mt-5 text-right">
            <button onClick={handlePrint} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>

        <h3 className="text-2xl text-purple-600 font-bold mt-7 mb-1">
          Activity: Collaboration Trivia
        </h3>
        <CourseQuestionCard
          submittedAnswer={userCourseQuestionResponseContext[
            chapterQuestions[0].course_id
          ]?.chapters[chapter.id]?.questions?.at(0)}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmitAnswer={handleCourseAnswerSubmit}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onRetakeQuestion={handleQuestionRetake}
          question={chapterQuestions[0]}
        />

        <h3 className="text-2xl mt-2  text-center font-light mb-5">
          Think being collaborative isn’t a big deal? Think again! Click below to learn more about
          Collaboration in Action.
        </h3>
        <button
          disabled={isPageDirty}
          onClick={handleNextChapterClick}
          className="btn-internal w-full"
        >
          Collaboration in Action
        </button>
      </div>
    </>
  );
};
